/**
 * Decide whether a message row should render in "compact" mode (no
 * avatar/name header — visually grouped under the previous row).
 *
 * Standard chat grouping: same human author within a 5-minute window.
 * Two carve-outs prevent automation attribution from flickering:
 *   1. If THIS message is automation-authored, never compact. A
 *      cron-fired automation lands ±10s around its scheduled minute,
 *      so a 5-min cron can produce inter-message gaps of 4m58s vs
 *      5m02s on adjacent fires — without this, every other run would
 *      randomly hide its sender header.
 *   2. If the PREVIOUS message is automation-authored, never compact a
 *      human reply under it. A human follow-up to a bot post is a
 *      different "voice" even when they share a `user_id` (e.g. a
 *      personal automation owner replying to their own bot).
 *
 * Stronger grouping (same automation_id across runs, regardless of
 * time) needs a `metadata.automation_id` field that the messages
 * schema doesn't carry today. Revisit if/when added — at that point
 * scheduled-series posts could group again, just keyed on the run
 * lineage instead of on time.
 */

const COMPACT_WINDOW_MS = 5 * 60 * 1000;

export interface CompactGroupingMessage {
  user_id: string;
  created_at: number;
  metadata?: { agent?: boolean } | null;
  /** Discord-shape inline-reply citation FK. Non-null ⇒ never compact:
   *  inline replies always render with full avatar + name so the row
   *  reads as a self-contained quote-cited reply, not a continuation
   *  of the previous message. Without this, a reply right after one
   *  of your own messages would lose the avatar (the "missing avatar"
   *  bug from PR3 staging). */
  replied_to_message_id?: string | null;
  /** Message `kind`. System-like kinds (`system`, `call_started_*`,
   *  `call_ended`) render as full-width dividers / pills and must
   *  never compact — they have no avatar to fold into. */
  kind?: string | null;
}

/**
 * Inline copy of the shared `isSystemLike` predicate. The shared
 * version takes a `MessageKind` (a closed union type); we accept
 * `string | null | undefined` here so this helper stays decoupled
 * from the typed enum and can be unit-tested in isolation.
 */
function isSystemLikeKind(kind: string | null | undefined): boolean {
  return (
    kind === "system" ||
    kind === "call_started_video" ||
    kind === "call_started_audio" ||
    kind === "call_ended"
  );
}

/**
 * Local equivalent of `lib/time-utils.isSameDay` — defined inline so
 * this helper has no React or app-level dependencies and can be
 * unit-tested in isolation.
 */
function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

export function isMessageCompact(
  item: CompactGroupingMessage,
  prev: CompactGroupingMessage | null | undefined,
): boolean {
  if (!prev) return false;
  // Inline replies and system-like rows are visually self-contained —
  // they need their own avatar / divider treatment.
  if (item.replied_to_message_id != null) return false;
  if (isSystemLikeKind(item.kind) || isSystemLikeKind(prev.kind)) return false;
  const isItemAutomated = item.metadata?.agent === true;
  const isPrevAutomated = prev.metadata?.agent === true;
  if (isItemAutomated || isPrevAutomated) return false;
  if (prev.user_id !== item.user_id) return false;
  const itemDate = new Date(item.created_at);
  const prevDate = new Date(prev.created_at);
  if (!isSameDay(itemDate, prevDate)) return false;
  return itemDate.getTime() - prevDate.getTime() < COMPACT_WINDOW_MS;
}
